import { AppFeature, InfringementSeverity, InfringementType } from '@repo/shared'
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import i18n from 'i18next'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Filter,
  Info,
  Loader2,
  ShieldAlert,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ListInfringementsParams } from '@repo/api'
import type { DateRange } from 'react-day-picker'

import PageHeader from '@/components/layout/page-header'
import BetaBadge from '@/components/shared/beta-badge'
import { DateRangePicker } from '@/components/shared/date-range-picker'
import { ListPagination } from '@/components/shared/list-pagination'
import { useDownloadReport } from '@/features/compliance/hooks/use-download-report'
import {
  useAcknowledgeInfringement,
  useInfringementStats,
  useInfringements,
} from '@/features/compliance/hooks/use-infringements'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { localizedPageTitle } from '@/lib/page-title'
import { FeatureGate } from '@/components/shared/feature-gate'

export const Route = createFileRoute('/dashboard/compliance/')({
  head: () => ({ meta: [{ title: localizedPageTitle('compliance') }] }),
  component: () => (
    <FeatureGate feature={AppFeature.HosCompliance}>
      <CompliancePage />
    </FeatureGate>
  ),
})

const SEVERITY_CONFIG: Record<
  string,
  { color: string; bg: string; label: string }
> = {
  [InfringementSeverity.Warning]: {
    color: 'text-yellow-600 dark:text-yellow-400',
    bg: 'bg-yellow-500/10',
    label: 'warning',
  },
  [InfringementSeverity.Serious]: {
    color: 'text-orange-600 dark:text-orange-400',
    bg: 'bg-orange-500/10',
    label: 'serious',
  },
  [InfringementSeverity.VerySeriousIndividual]: {
    color: 'text-destructive',
    bg: 'bg-destructive/10',
    label: 'verySeriousIndividual',
  },
  [InfringementSeverity.VerySeriousCompany]: {
    color: 'text-destructive',
    bg: 'bg-destructive/10',
    label: 'verySeriousCompany',
  },
}

function formatExcess(seconds: number | null): string {
  if (seconds == null) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h === 0) return `${m}min`
  return `${h}h ${m}min`
}

function CompliancePage() {
  useDocumentTitle('compliance')
  const { t } = useTranslation('compliance')
  const [filters, setFilters] = useState<ListInfringementsParams>({
    limit: 10,
    offset: 0,
  })

  const [reportType, setReportType] = useState<'driver' | 'fleet'>('fleet')
  const [reportRange, setReportRange] = useState<DateRange | undefined>({
    from: new Date(Date.now() - 30 * 24 * 3600 * 1000),
    to: new Date(),
  })
  const [reportDriverCard, setReportDriverCard] = useState('')

  const { data: stats, isLoading: statsLoading } = useInfringementStats()
  const { data, isLoading } = useInfringements(filters)
  const acknowledge = useAcknowledgeInfringement()
  const downloadReport = useDownloadReport()

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        hideBackButton
        titleBadge={<BetaBadge />}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {statsLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))
        ) : (
          <>
            <div className="rounded-xl border p-4">
              <div className="text-muted-foreground mb-1 flex items-center gap-2 text-xs font-medium">
                <AlertTriangle className="h-4 w-4" />
                {t('totalInfringements')}
              </div>
              <p className="text-3xl font-bold">{stats?.total ?? 0}</p>
              <p className="text-muted-foreground text-xs">{t('last30Days')}</p>
            </div>
            <div className="rounded-xl border p-4">
              <div className="mb-1 flex items-center gap-2 text-xs font-medium text-orange-600 dark:text-orange-400">
                <XCircle className="h-4 w-4" />
                {t('unacknowledged')}
              </div>
              <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">
                {stats?.unacknowledged ?? 0}
              </p>
              <p className="text-muted-foreground text-xs">
                {t('requiresAttention')}
              </p>
            </div>
            <div className="rounded-xl border p-4">
              <div className="text-destructive mb-1 flex items-center gap-2 text-xs font-medium">
                <ShieldAlert className="h-4 w-4" />
                {t('seriousViolations')}
              </div>
              <p className="text-destructive text-3xl font-bold">
                {stats?.serious ?? 0}
              </p>
              <p className="text-muted-foreground text-xs">
                {t('seriousOrAbove')}
              </p>
            </div>
          </>
        )}
      </div>

      {/* Export Reports */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border p-4">
        <span className="text-sm font-medium">{t('exportReport')}</span>
        <Select
          value={reportType}
          onValueChange={(v) => setReportType(v as 'driver' | 'fleet')}
        >
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="fleet">{t('fleetReport')}</SelectItem>
            <SelectItem value="driver">{t('driverReport')}</SelectItem>
          </SelectContent>
        </Select>
        {reportType === 'driver' && (
          <input
            type="text"
            placeholder={t('driverCardCodePlaceholder')}
            value={reportDriverCard}
            onChange={(e) => setReportDriverCard(e.target.value)}
            className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm sm:w-auto"
          />
        )}
        <DateRangePicker
          value={reportRange}
          onChange={setReportRange}
          clearable={false}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={
            downloadReport.isPending ||
            !reportRange?.from ||
            !reportRange.to ||
            (reportType === 'driver' && !reportDriverCard)
          }
          onClick={() =>
            downloadReport.mutate({
              reportType,
              driverCardCode:
                reportType === 'driver' ? reportDriverCard : undefined,
              from: format(reportRange!.from!, 'yyyy-MM-dd'),
              to: format(reportRange!.to!, 'yyyy-MM-dd'),
              format: 'pdf',
              locale: i18n.language,
            })
          }
          className="text-sm"
        >
          {downloadReport.isPending ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileText className="mr-1.5 h-3.5 w-3.5" />
          )}
          PDF
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={
            downloadReport.isPending ||
            !reportRange?.from ||
            !reportRange.to ||
            (reportType === 'driver' && !reportDriverCard)
          }
          onClick={() =>
            downloadReport.mutate({
              reportType,
              driverCardCode:
                reportType === 'driver' ? reportDriverCard : undefined,
              from: format(reportRange!.from!, 'yyyy-MM-dd'),
              to: format(reportRange!.to!, 'yyyy-MM-dd'),
              format: 'csv',
              locale: i18n.language,
            })
          }
          className="text-sm"
        >
          <Download className="mr-1.5 h-3.5 w-3.5" />
          CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
          <Filter className="h-4 w-4" />
          {t('filters')}
        </div>
        <Select
          value={filters.type ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              type: v === 'all' ? undefined : (v as InfringementType),
              offset: 0,
            }))
          }
        >
          <SelectTrigger className="w-full sm:w-[220px]">
            <SelectValue placeholder={t('allTypes')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allTypes')}</SelectItem>
            {Object.values(InfringementType).map((type) => (
              <SelectItem key={type} value={type}>
                {t(`type.${type}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.severity ?? 'all'}
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              severity: v === 'all' ? undefined : (v as InfringementSeverity),
              offset: 0,
            }))
          }
        >
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder={t('allSeverities')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allSeverities')}</SelectItem>
            {Object.values(InfringementSeverity).map((sev) => (
              <SelectItem key={sev} value={sev}>
                {t(`severity.${sev}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={
            filters.acknowledged == null
              ? 'all'
              : filters.acknowledged
                ? 'true'
                : 'false'
          }
          onValueChange={(v) =>
            setFilters((f) => ({
              ...f,
              acknowledged: v === 'all' ? undefined : v === 'true',
              offset: 0,
            }))
          }
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder={t('allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('allStatuses')}</SelectItem>
            <SelectItem value="false">{t('unacknowledgedOnly')}</SelectItem>
            <SelectItem value="true">{t('acknowledgedOnly')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border">
        <div className="min-w-[700px]">
          <div className="text-muted-foreground grid grid-cols-[1fr_1fr_7rem_5.5rem_7.5rem_8rem] gap-4 border-b px-4 py-3 text-xs font-medium">
            <span>{t('violationType')}</span>
            <span>{t('driverColumn')}</span>
            <span>{t('severityColumn')}</span>
            <span>{t('excessColumn')}</span>
            <span>{t('dateColumn')}</span>
            <span>{t('statusColumn')}</span>
          </div>

          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="border-b px-4 py-3">
                <Skeleton className="h-5 w-full" />
              </div>
            ))}

          {!isLoading && data?.items.length === 0 && (
            <div className="text-muted-foreground flex flex-col items-center gap-2 py-12 text-center text-sm">
              <CheckCircle2 className="h-10 w-10 opacity-30" />
              <p>{t('noInfringements')}</p>
            </div>
          )}

          {data?.items.map((item) => {
            const sev = SEVERITY_CONFIG[item.severity]
            return (
              <div
                key={item.id}
                className="grid grid-cols-[1fr_1fr_7rem_5.5rem_7.5rem_8rem] items-center gap-4 border-b px-4 py-3 text-sm last:border-b-0"
              >
                <div className="flex items-center gap-1.5">
                  <p className="font-medium">{t(`type.${item.type}`)}</p>
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <Info className="text-muted-foreground h-3 w-3 shrink-0 cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-56 text-wrap">
                      {t(`typeTooltip.${item.type}`)}
                    </TooltipContent>
                  </Tooltip>
                </div>
                <div>
                  <p className="text-xs">{item.driverName ?? '—'}</p>
                  <p className="text-muted-foreground text-[10px]">
                    {item.driverCardCode ?? '—'}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className={cn('text-[10px]', sev.color, sev.bg)}
                >
                  {t(`severity.${item.severity}`)}
                </Badge>
                <span className="text-xs font-medium tabular-nums">
                  {formatExcess(item.excessSeconds)}
                </span>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span className="text-muted-foreground flex items-center gap-1 text-xs">
                      <Clock className="h-3 w-3" />
                      {format(new Date(item.occurredAt), 'dd/MM HH:mm')}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    {format(new Date(item.occurredAt), 'dd/MM/yyyy HH:mm:ss')}
                  </TooltipContent>
                </Tooltip>
                <div>
                  {item.acknowledged ? (
                    <Badge
                      variant="outline"
                      className="bg-green-500/10 text-[10px] text-green-600 dark:text-green-400"
                    >
                      <CheckCircle2 className="mr-0.5 h-3 w-3" />
                      {t('acknowledged')}
                    </Badge>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      disabled={acknowledge.isPending}
                      onClick={() => acknowledge.mutate({ id: item.id })}
                    >
                      {t('acknowledge')}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {data &&
        (() => {
          const pageSize = filters.limit ?? 10
          const offset = filters.offset ?? 0
          const page = Math.floor(offset / pageSize) + 1
          const totalPages = Math.max(1, Math.ceil(data.total / pageSize))
          return (
            <ListPagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={data.total}
              hasPrevious={page > 1}
              hasNext={page < totalPages}
              onPageChange={(p) =>
                setFilters((f) => ({
                  ...f,
                  offset: (p - 1) * (f.limit ?? 10),
                }))
              }
              onPageSizeChange={(size) =>
                setFilters((f) => ({ ...f, limit: size, offset: 0 }))
              }
              className="px-4"
            />
          )
        })()}
    </div>
  )
}
